"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserDeletedContract = exports.UserUpdatedContract = exports.SetBusinessStatusContract = exports.UserPresenceRoutes = exports.UserDeletedEventV1 = exports.UserUpdatedEventV1 = exports.MyProfileReplyV1 = exports.MyProfileQueryV1 = exports.MySipCredentialsReplyV1 = exports.MySipCredentialsV1 = exports.MySipCredentialsQueryV1 = exports.BindSipUserCommandV1 = exports.SetBusinessStatusReplyV1 = exports.SetBusinessStatusCommandV1 = exports.UserSnapshot = exports.UserPresenceSubjects = exports.SipStatus = void 0;
const zod_1 = require("zod");
const message_1 = require("./message");
const subject_contract_1 = require("./subject-contract");
const ack_reply_1 = require("./ack-reply");
/**
 * SIP-status — derived состояние из SIP-регистрации и активных каналов.
 * Заполняется автоматически `UserPresenceService` по `evt.sbc.sipuser.*` +
 * `evt.cu.call.*`. UI/стратегии его НЕ мутируют напрямую.
 */
exports.SipStatus = zod_1.z.enum(['ready', 'offline', 'in_call', 'ringing']);
const SipSbcUnitId = zod_1.z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9_-]*$/);
// ── Subjects ──
exports.UserPresenceSubjects = {
    setBusinessStatusCommand: 'cmd.user.set_business_status',
    userUpdatedEvent: 'evt.user.updated',
    userDeletedEvent: 'evt.user.deleted',
};
// ── Shared snapshot (используется в reply UsersListReply и в evt.user.updated) ──
/**
 * Полный snapshot пользователя. Несётся в `evt.user.updated` целиком (не дельта),
 * чтобы DataSource просто заменял item в Map по ключу.
 */
exports.UserSnapshot = zod_1.z.object({
    id: zod_1.z.string(),
    /** Логин (ключ входа, `UNIQUE(username, tenant_id)`). Для показа — `displayName`. */
    username: zod_1.z.string(),
    /**
     * Человекочитаемое имя («Иванов И.И.»). `null` — не задано: показывать
     * `username` как fallback (в UI — хелпер `userLabel`). Тёзки допустимы.
     */
    displayName: zod_1.z.string().nullable(),
    tenantId: zod_1.z.string(),
    roles: zod_1.z.array(zod_1.z.string()),
    isActive: zod_1.z.boolean(),
    /** Локальная identity SIP-точки; валидна только вместе с sipSbcUnitId. */
    sipUserId: zod_1.z.number().int().positive().nullable(),
    /** SBC-контур, в runtime-БД которого живёт sipUserId. */
    sipSbcUnitId: SipSbcUnitId.nullable(),
    /** Derived из SIP-регистрации и активных каналов. */
    sipStatus: exports.SipStatus,
    sipStatusChangedAt: zod_1.z.string().datetime(),
    /**
     * Бизнес-статус. **Свободная строка**, не enum.
     * Соглашение платформы: `'ready'` — доступен для распределения звонков.
     * Любые другие значения (`'break'`, `'leaving'`, `'lunch'`, `'meeting'`, ...)
     * стратегия видит как «не ready» = пропускает.
     */
    businessStatus: zod_1.z.string(),
    businessStatusChangedAt: zod_1.z.string().datetime(),
    /**
     * UI-флаг: показывать пользователю коммуникационную панель. **Не право** —
     * права за ролями (`@omniflow/access`); применяет хост панели. Дефолт `true`.
     */
    panelEnabled: zod_1.z.boolean(),
    /**
     * UI-флаг: чат в панели. Хранится независимо от `panelEnabled` (выключение
     * панели не теряет его значение); эффективно = `panelEnabled && panelChatEnabled`.
     */
    panelChatEnabled: zod_1.z.boolean(),
    /**
     * UI-флаг: софтфон в панели. Хранится независимо от `panelEnabled`;
     * эффективно = `panelEnabled && panelSoftphoneEnabled`.
     */
    panelSoftphoneEnabled: zod_1.z.boolean(),
    createdAt: zod_1.z.string().datetime(),
});
// ── Commands ──
/**
 * Установить бизнес-статус пользователя.
 *
 * Источники: UI (кнопка), DTMF в стратегии управления, любой динамический сервис.
 * Значение `businessStatus` — свободная строка; платформа не валидирует список.
 */
exports.SetBusinessStatusCommandV1 = message_1.CommandMessage.extend({
    type: zod_1.z.literal('SetBusinessStatusCommand'),
    version: zod_1.z.literal(1),
    /** ID пользователя чей статус меняем. */
    userId: zod_1.z.string().min(1),
    /**
     * Тенант пользователя — отдельное поле от envelope.tenantId (envelope.tenantId =
     * tenant отправителя, перезаписывается autoinject'ом на бэке).
     */
    targetTenantId: zod_1.z.string().min(1),
    businessStatus: zod_1.z.string().min(1).max(64),
    /**
     * CAS-предикат (FABOLA-ARCH-FIX 2.5): применить ТОЛЬКО если текущий
     * business_status равен этому значению. При несовпадении — `changed:false`
     * (не ошибка). Для межинстансных гонок: N подписчиков события делают CAS,
     * side-effect (перезвон) выполняет только тот, у кого `changed:true`.
     */
    expectedBusinessStatus: zod_1.z.string().min(1).max(64).optional(),
});
/**
 * `changed:false` — статус уже был таким ЛИБО CAS-предикат не совпал
 * (для CAS-вызовов это сигнал «гонка проиграна, side-effect не делать»).
 */
exports.SetBusinessStatusReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({ isOk: zod_1.z.literal(true), changed: zod_1.z.boolean() }),
    zod_1.z.object({ isOk: zod_1.z.literal(false), error: zod_1.z.string() }),
]);
/**
 * Привязать (или отвязать) SIP-точку доступа к пользователю — `core.users.sip_user_id`.
 * Для hot-desk логина оператора: оператор звонит с телефона → стратегия привязывает
 * его user к sipuser этого телефона. Связка 1:1 (глобальный unique по `sip_user_id`):
 * прежний владелец вытесняется атомарно (см. `UsersService.updateSipUserId`).
 *
 * Отдельная команда (а не `cmd.admin.user.update`), чтобы роль `strategy` могла
 * привязывать БЕЗ admin-прав — по аналогии с `cmd.user.set_business_status`.
 */
exports.BindSipUserCommandV1 = message_1.CommandMessage.extend({
    type: zod_1.z.literal('BindSipUserCommand'),
    version: zod_1.z.literal(1),
    /** ID пользователя, которому привязываем SIP-точку. */
    userId: zod_1.z.string().min(1),
    /** Тенант пользователя (отдельно от envelope.tenantId отправителя). */
    targetTenantId: zod_1.z.string().min(1),
    /** sipuser.id (kamailio subscriber id). `null` — отвязать. */
    sipUserId: zod_1.z.number().int().positive().nullable(),
    /** SBC-контур точки. `null` допустим только при отвязке. */
    sipSbcUnitId: SipSbcUnitId.nullable(),
}).superRefine((value, ctx) => {
    if ((value.sipUserId === null) !== (value.sipSbcUnitId === null)) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['sipSbcUnitId'], message: 'sipUserId и sipSbcUnitId задаются парой' });
    }
});
/**
 * SIP-креды СВОЕЙ точки для браузерного софтфона (эпик CPANEL). Актор — ТОЛЬКО
 * из envelope (identity server-side, `Nats-Request-Info`/ALS) — своя точка by
 * construction, тело команды не несёт `userId`.
 */
exports.MySipCredentialsQueryV1 = message_1.QueryMessage.extend({
    type: zod_1.z.literal('MySipCredentialsQuery'),
    version: zod_1.z.literal(1),
});
exports.MySipCredentialsV1 = zod_1.z.object({
    sipUserId: zod_1.z.number().int(),
    sipSbcUnitId: SipSbcUnitId,
    username: zod_1.z.string(),
    /** `null` — легаси-запись без сохранённого plaintext (до миграции 82). */
    password: zod_1.z.string().nullable(),
    /** SIP realm = tenantId по конвенции проекта. */
    domain: zod_1.z.string(),
    wssUrl: zod_1.z.string(),
    phoneNumber: zod_1.z.string().nullable(),
});
exports.MySipCredentialsReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({ isOk: zod_1.z.literal(true), credentials: exports.MySipCredentialsV1 }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(['forbidden', 'not_bound', 'not_found']),
        error: zod_1.z.string().optional(),
    }),
]);
/**
 * Снапшот СВОЕГО пользователя. Актор — ТОЛЬКО из envelope (как
 * `q.user.my_sip_credentials`): тело не несёт `userId`, чужой профиль не
 * спросить by construction. Зачем отдельный query: оператору без `pulse:view`
 * недоступен `q.admin.users.list`, а свой профиль (например UI-флаги панели)
 * нужен любому человеку.
 */
exports.MyProfileQueryV1 = message_1.QueryMessage.extend({
    type: zod_1.z.literal('MyProfileQuery'),
    version: zod_1.z.literal(1),
});
exports.MyProfileReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({ isOk: zod_1.z.literal(true), user: exports.UserSnapshot }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(['not_found']),
        error: zod_1.z.string().optional(),
    }),
]);
// ── Events ──
exports.UserUpdatedEventV1 = message_1.EventMessage.extend({
    type: zod_1.z.literal('UserUpdatedEvent'),
    version: zod_1.z.literal(1),
    user: exports.UserSnapshot,
});
exports.UserDeletedEventV1 = message_1.EventMessage.extend({
    type: zod_1.z.literal('UserDeletedEvent'),
    version: zod_1.z.literal(1),
    userId: zod_1.z.string(),
    /** Tenant удалённого пользователя. */
    userTenantId: zod_1.z.string(),
});
// ── Routes (subject + input schema + reply schema атомом) ──
/**
 * Routes-таблица domain'а user-presence. Содержит весь домен в одной декларации:
 * subject + input/reply schemas + kind. Используется через bus.request/respond/emit/on.
 */
exports.UserPresenceRoutes = {
    setBusinessStatus: (0, subject_contract_1.defineCommand)('cmd.user.set_business_status', exports.SetBusinessStatusCommandV1, exports.SetBusinessStatusReplyV1),
    bindSipUser: (0, subject_contract_1.defineCommand)('cmd.user.bind_sipuser', exports.BindSipUserCommandV1, ack_reply_1.AckReplyV1),
    /** SIP-креды СВОЕЙ точки (эпик CPANEL) — актор из envelope, «только свои». */
    mySipCredentials: (0, subject_contract_1.defineQuery)('q.user.my_sip_credentials', exports.MySipCredentialsQueryV1, exports.MySipCredentialsReplyV1),
    /** Снапшот СВОЕГО пользователя — актор из envelope, «только свой». */
    myProfile: (0, subject_contract_1.defineQuery)('q.user.my_profile', exports.MyProfileQueryV1, exports.MyProfileReplyV1),
    userUpdated: (0, subject_contract_1.defineEvent)('evt.user.updated', exports.UserUpdatedEventV1),
    userDeleted: (0, subject_contract_1.defineEvent)('evt.user.deleted', exports.UserDeletedEventV1),
};
// ── Contracts (DEPRECATED — миграция на UserPresenceRoutes) ──
/** @deprecated use `UserPresenceRoutes.setBusinessStatus` */
// eslint-disable-next-line @typescript-eslint/no-deprecated
exports.SetBusinessStatusContract = (0, subject_contract_1.defineCommandContract)(exports.UserPresenceSubjects.setBusinessStatusCommand, exports.SetBusinessStatusCommandV1);
/** @deprecated use `UserPresenceRoutes.userUpdated` */
// eslint-disable-next-line @typescript-eslint/no-deprecated
exports.UserUpdatedContract = (0, subject_contract_1.defineEventContract)(exports.UserPresenceSubjects.userUpdatedEvent, exports.UserUpdatedEventV1);
/** @deprecated use `UserPresenceRoutes.userDeleted` */
// eslint-disable-next-line @typescript-eslint/no-deprecated
exports.UserDeletedContract = (0, subject_contract_1.defineEventContract)(exports.UserPresenceSubjects.userDeletedEvent, exports.UserDeletedEventV1);
//# sourceMappingURL=user-presence.js.map